import { Divider, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { COUNTDOWN_TIMER_WIDGET_TYPE, DEFAULT_COUNTDOWN_SECONDS } from '../../../../utils/countdownTimerCodeUtils';
import { Trans, useTranslation } from 'react-i18next';
import DraggableComponent from '../../DraggableComponent';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import type { DriveUnitPortOccupancy } from '../../../../redux/ApplicationSlice';
import {
  buildElectronicsSections,
  ELECTRONIC_OTHER_ELECTRONICS_DRAWER_KIND,
  filterMotorPortOccupancyRows,
  getConfiguredSensorClassNames,
  getMechanismPickerEntries,
  getMotorPickerEntries,
} from '../../../../utils/electronicsDashboardUtils';
import { sensorDrawerSectionTitle } from '../../../../Components/Graphs/sensor-utils';
import { DRIVE_UNIT_PORT_PARAMETERS } from '../../../../utils/driveUnitPortSelection';
import ElectronicsSectionCollapsible from './ElectronicsSectionCollapsible';
import MechanismDrawerSection from './MechanismDrawerSection';
import MotorAdvancedDrawerSection from './MotorAdvancedDrawerSection';
import MotorMetricsDrawerSection from './MotorMetricsDrawerSection';
import MotorPerMotorDrawerSection from './MotorPerMotorDrawerSection';
import SensorTypeDrawerSection from './SensorTypeDrawerSection';
import DashboardDrawerTabDescription from '../../DashboardDrawerTabDescription';

const SENSOR_PORT_PARAMETER_SUFFIX = `:${DRIVE_UNIT_PORT_PARAMETERS.SENSOR}`;

export type MechanismDataElectronicsKind = 'motor' | 'sensor' | 'other';

export type MechanismDataContentProps = {
  electronicsKind: MechanismDataElectronicsKind;
};

const MotorDataCategoryEmptyState = ({ i18nKey, defaultText }: { i18nKey: string; defaultText: string }) => (
  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, px: 1 }}>
    <Trans i18nKey={i18nKey}>{defaultText}</Trans>
  </Typography>
);

const MechanismDataContent = ({ electronicsKind }: MechanismDataContentProps) => {
  const { t } = useTranslation();
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');

  const motorPorts: DriveUnitPortOccupancy[] = useMemo(() => filterMotorPortOccupancyRows(driveUnitPorts), [driveUnitPorts]);

  const sensorPorts: DriveUnitPortOccupancy[] = useMemo(
    () => driveUnitPorts.filter((row: DriveUnitPortOccupancy) => row.electronic.endsWith(SENSOR_PORT_PARAMETER_SUFFIX)),
    [driveUnitPorts]
  );

  const electronicsSections = useMemo(() => {
    if (electronicsKind === 'motor' || electronicsKind === 'sensor') {
      return [];
    }
    return buildElectronicsSections(motorPorts, projectCode, ELECTRONIC_OTHER_ELECTRONICS_DRAWER_KIND);
  }, [electronicsKind, motorPorts, projectCode]);

  const configuredSensorClassNames = useMemo(
    () => (electronicsKind === 'sensor' ? getConfiguredSensorClassNames(sensorPorts, projectCode) : []),
    [electronicsKind, sensorPorts, projectCode]
  );

  const motorPickerEntries = useMemo(
    () => (electronicsKind === 'motor' ? getMotorPickerEntries(motorPorts, projectCode) : []),
    [electronicsKind, motorPorts, projectCode]
  );

  const mechanismPickerEntries = useMemo(
    () => (electronicsKind === 'motor' ? getMechanismPickerEntries(motorPorts, projectCode) : []),
    [electronicsKind, motorPorts, projectCode]
  );

  const hasMotors = motorPickerEntries.length > 0;
  const hasMechanisms = mechanismPickerEntries.length > 0;
  const hasAdvancedElectronics = hasMotors || hasMechanisms;
  const hasElectronics =
    electronicsKind === 'motor'
      ? hasAdvancedElectronics
      : electronicsKind === 'sensor'
      ? configuredSensorClassNames.length > 0
      : electronicsSections.length > 0;

  return (
    <Stack direction="column" width="100%" spacing={1} sx={{ p: 2 }} alignItems={'stretch'}>
      <Stack direction="row" spacing={0} alignItems={'center'} sx={{ width: '100%' }}>
        <DashboardDrawerTabDescription>
          {electronicsKind === 'motor' ? (
            <Trans i18nKey="dashboard.drawer.motorData.description">Motor data for your robot build.</Trans>
          ) : electronicsKind === 'other' ? (
            <Trans i18nKey="dashboard.drawer.otherData.description">Other electronics data for your robot build.</Trans>
          ) : (
            <Trans i18nKey="dashboard.drawer.sensorData.description">Sensor data for your robot build.</Trans>
          )}
        </DashboardDrawerTabDescription>
      </Stack>

      {electronicsKind === 'motor' ? (
        <Stack alignItems="stretch" spacing={2} sx={{ width: '100%' }}>
          <ElectronicsSectionCollapsible sectionTitle={t('dashboard.drawer.motorData.category.mechanisms', 'Mechanisms')}>
            {hasMechanisms ? (
              <MechanismDrawerSection motorPorts={motorPorts} projectCode={projectCode} />
            ) : (
              <MotorDataCategoryEmptyState
                i18nKey="dashboard.drawer.motorData.empty.mechanisms"
                defaultText="Add a mechanism in your program code for widgets to appear here."
              />
            )}
          </ElectronicsSectionCollapsible>
          <Divider sx={{ width: '100%' }} />
          <ElectronicsSectionCollapsible sectionTitle={t('dashboard.drawer.motorData.category.motors', 'Motors')}>
            {hasMotors ? (
              <>
                <MotorMetricsDrawerSection motorPorts={motorPorts} projectCode={projectCode} />
                <MotorPerMotorDrawerSection motorPorts={motorPorts} projectCode={projectCode} />
              </>
            ) : (
              <MotorDataCategoryEmptyState
                i18nKey="dashboard.drawer.motorData.empty.motors"
                defaultText="Add a motor in your program code for widgets to appear here."
              />
            )}
          </ElectronicsSectionCollapsible>
          <Divider sx={{ width: '100%' }} />
          <ElectronicsSectionCollapsible sectionTitle={t('dashboard.drawer.motorData.category.advanced', 'Advanced')}>
            {hasAdvancedElectronics ? (
              <MotorAdvancedDrawerSection motorPorts={motorPorts} projectCode={projectCode} />
            ) : (
              <MotorDataCategoryEmptyState
                i18nKey="dashboard.drawer.motorData.empty.advanced"
                defaultText="Add a motor or mechanism in your program code for multi-axis widgets to appear here."
              />
            )}
          </ElectronicsSectionCollapsible>
        </Stack>
      ) : null}

      {electronicsKind === 'sensor' ? (
        <Stack alignItems="stretch" spacing={2} sx={{ width: '100%' }}>
          {configuredSensorClassNames.map((sensorClassName, index) => (
            <Stack key={sensorClassName} spacing={0} alignItems="stretch">
              {index > 0 ? <Divider sx={{ width: '100%' }} /> : null}
              <ElectronicsSectionCollapsible sectionTitle={sensorDrawerSectionTitle(sensorClassName, t)}>
                <SensorTypeDrawerSection sensorPorts={sensorPorts} projectCode={projectCode} sensorClassName={sensorClassName} />
              </ElectronicsSectionCollapsible>
            </Stack>
          ))}
        </Stack>
      ) : null}

      {electronicsKind === 'other' && hasElectronics
        ? electronicsSections.map((section, index) => (
            <Stack key={section.electronic} spacing={0} alignItems="stretch">
              {index > 0 ? <Divider sx={{ width: '100%', my: 1 }} /> : null}
              <ElectronicsSectionCollapsible sectionTitle={section.sectionTitle} widgets={section.widgets} />
            </Stack>
          ))
        : null}

      {electronicsKind === 'other' ? (
        <>
          {hasElectronics ? <Divider sx={{ width: '100%', my: 1 }} /> : null}
          <Stack alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <DraggableComponent
              id="Console Widget"
              type="console"
              dataKey="all"
              minW={3}
              title={t('dashboard.draggableComponent.title.console', 'Console Widget')}
              sx={{ m: 0 }}
            />
            <DraggableComponent
              id="Timer Widget"
              type="timer"
              dataKey="program"
              minW={2}
              title={t('dashboard.draggableComponent.title.timer', 'Timer Widget')}
              sx={{ m: 0 }}
            />
            <DraggableComponent
              id="Countdown Timer Widget"
              type={COUNTDOWN_TIMER_WIDGET_TYPE}
              dataKey="program"
              minW={2}
              options={{ countdownSeconds: DEFAULT_COUNTDOWN_SECONDS }}
              title={t('dashboard.draggableComponent.title.countdownTimer', 'Countdown Timer')}
              sx={{ m: 0 }}
            />
          </Stack>
        </>
      ) : null}

      {!hasElectronics && electronicsKind !== 'motor' && electronicsKind !== 'other' && (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            <Trans i18nKey="dashboard.drawer.sensorData.noElectronics">No sensors detected in project code.</Trans>
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default MechanismDataContent;
